import { packageCustomVersion } from '@/api/dmaker/custom'
import type { TranslateResult } from 'vue-i18n'
import type { AppsType, AppInfo } from './shared'
import { getGmakerProjectInfo } from '@pt/api/gmaker/project.js'
import { getDmakerTemplateDetail } from '@pt/api/dmaker/index.js'
import { getCampaignSets } from '@pt/api/user/index'
import { downloadPtPlayable } from '@pt/api/creative/index'
import {
  getGmakerProjectChannels,
  getGmakerDownloadStatus,
  packGmakerProject,
  downloadCommonPlayable,
  getGmakerCopyLink,
} from '@pt/api/gmaker/project.js'
import {
  getDmakerCustomInfo,
  fetchVersionChannelList,
  fetchVersionDownloadStatus,
  fetchTemplateChannelList,
  downloadCustomPlayable,
  getDmakerCopyLink,
} from '@/api/creative/custom.js'
import {
  getPSProjectInfo,
  downloadPSPlayable,
  getPsProjectChannels,
  packPsProject,
  getPsDownloadStatus,
  getPsProjectDownloadCount,
} from '@ps/api/project.js'
import i18n from '@/lang'
import _ from 'lodash'

export enum PushNameCode {
  // 项目ID
  ProjectId = 'project_id',

  // 项目名称
  ProjectName = 'project_name',

  // 版本名称
  Version = 'version_name',

  // 作品名称
  OutputName = 'output_name',

  // 手机系统
  Platform = 'platform',

  // 自动序号
  AutoIncrement = 'auto_increment',

  // 创意版本
  CreativeVersion = 'creative_version',
}

export interface MaterialNameOption {
  code: PushNameCode
  name: string
  label: TranslateResult
}

export interface PushNameOption extends MaterialNameOption {
  disabled?: boolean
}

export enum PtBusinessType {
  // 可玩项目-普通制作
  Normal = 'normal',

  // 可玩项目-自由编辑
  Playsmart = 'playsmart',

  // 创意服务-定制可玩-普通制作
  CustomNormal = 'custom_normal',

  // 创意服务-定制视频图片
  CustomVideoImage = 'custom_video_image',

  // 视频项目
  Video = 'video',

  // 互动视频编辑器
  Iv = 'iv',
}

export interface PackResponse {
  list: any[]
}
export interface PtBusinessConfig {
  // 是否展示下载剩余量
  showDownloadBalance?: boolean

  // 是否有包体控制
  enablePackageQuality?: boolean

  productFrom?: 'commonTemplate' | 'ps'

  packRequest?: (...args: any[]) => Promise<PackResponse>
  copyLinkRequest?: (...args: any[]) => Promise<PackResponse>
  channelListRequest?: (...args: any[]) => Promise<PackResponse>
  downloadStatusRequest?: (...args: any[]) => Promise<PackResponse>
  downloadRequest?: (...args: any[]) => Promise<PackResponse>
  getRowData?: any
  projectCountRequest?: any

  // 打包接口

  // 后缀名
  nameSuffixCodes?: PushNameCode[]

  // 获取项目的产品信息
  getApps?: (
    id: any,
    options?: {
      isDefaultVersion?: boolean
      templateId?: any
    },
  ) => Promise<AppsType>
}

export const PtBusinessConfigs: Record<PtBusinessType, PtBusinessConfig> = {
  [PtBusinessType.Normal]: {
    showDownloadBalance: true,
    enablePackageQuality: true,
    productFrom: 'commonTemplate',
    downloadRequest: (...args) =>
      downloadPtPlayable(...args).then((res) => res.data.data),
    channelListRequest: (...args) =>
      getGmakerProjectChannels(...args).then((res) => res.data.data),
    packRequest: (...args) =>
      packGmakerProject(...args).then((res) => res.data.data),
    downloadStatusRequest: (...args) =>
      getGmakerDownloadStatus(...args).then((res) => res.data.data),
    nameSuffixCodes: [PushNameCode.Platform],
    getApps: (id: any) =>
      resolveApps(
        getGmakerProjectInfo({
          project_id: id,
        }).then((res) => {
          return (
            res.data.data.product_info?.app_info?.map((item: any) => ({
              package_name: item.package_name,
              platform: item.platform,
            })) ?? []
          )
        }),
      ),
    getRowData(item: any) {
      return _.pick(item, ['template_id', 'id'])
    },
  },
  [PtBusinessType.Playsmart]: {
    showDownloadBalance: true,
    enablePackageQuality: true,
    productFrom: 'ps',
    downloadRequest: (...args) =>
      downloadPtPlayable(...args).then((res) => res.data.data),
    packRequest: (...args) =>
      packPsProject(...args).then((res: any) => res.data),
    channelListRequest: (...args) =>
      getPsProjectChannels(...args).then((res: any) => res.data),
    projectCountRequest: getPsProjectDownloadCount,
    downloadStatusRequest: (...args) =>
      getPsDownloadStatus(...args).then((res: any) => res.data),
    nameSuffixCodes: [PushNameCode.Platform],
    getApps: (id: any) =>
      resolveApps(
        getPSProjectInfo({
          id: id,
        }).then((res: any) => {
          return (
            res.data.product_info?.app_info?.map((item: any) => ({
              package_name: item.package_name,
              platform: item.platform,
            })) ?? []
          )
        }),
      ),
    getRowData(item: any) {
      return _.pick(item, ['template_id', 'id'])
    },
  },
  [PtBusinessType.CustomNormal]: {
    enablePackageQuality: true, // TODO default_version 不支持
    packRequest: (...args) =>
      packageCustomVersion(...args).then((res) => res.data.data),
    downloadRequest: (...args) =>
      downloadPtPlayable(...args).then((res) => res.data.data),
    channelListRequest: (...args) =>
      fetchVersionChannelList(...args).then((res: any) => res.data.data),
    downloadStatusRequest: (...args) =>
      fetchVersionDownloadStatus(...args).then((res) => res.data.data),
    nameSuffixCodes: [PushNameCode.Platform],
    getApps: (id: any, options) =>
      resolveApps(
        options?.isDefaultVersion
          ? getDmakerTemplateDetail({
              template_id: options?.templateId ?? '',
            }).then((res) => {
              const product_info = res.data.data.product_info || {}
              return [
                {
                  package_name: product_info.package_names?.android,
                  platform: product_info.app_names?.android ? 'android' : '',
                },
                {
                  package_name: product_info.package_names?.ios,
                  platform: product_info.app_names?.ios ? 'ios' : '',
                },
              ].filter((item) => Boolean(item.platform))
            })
          : getDmakerCustomInfo({
              custom_id: id,
            }).then((res) => {
              return (
                res.data.data.apps?.map((item: any) => ({
                  package_name: item.package_name,
                  platform: item.platform,
                })) ?? []
              )
            }),
      ),
    getRowData(item: any) {
      return _.pick(item, [
        'template_id',
        'id',
        'is_myself',
        'source_type',
        'pushPermissions',
        'isPtVersion',
      ])
    },
  },
  [PtBusinessType.CustomVideoImage]: {},
  [PtBusinessType.Video]: {
    // 视频导出任务批量导出才需要，所以需要在pushDialog中判断
    nameSuffixCodes: [PushNameCode.AutoIncrement],
  },
}

function resolveApps(
  _apps: AppInfo[] | Promise<AppInfo[]>,
): ReturnType<Required<PtBusinessConfig>['getApps']> {
  return Promise.all([getCampaignSets(), Promise.resolve(_apps)]).then(
    ([campaignSetsRes, apps]) => {
      const campaignSets = campaignSetsRes.data.data.list

      const keys = Object.keys(
        campaignSets,
      ) as unknown as (keyof typeof campaignSets)[]

      keys.forEach((k) => {
        campaignSets[k] =
          campaignSets[k]?.filter((item) =>
            apps.some(
              (app) =>
                app.package_name === item.package_name &&
                app.platform === item.platform,
            ),
          ) ?? []
      })

      return campaignSets
    },
  )
}

export const DefaultNameOptions: MaterialNameOption[] = [
  {
    code: PushNameCode.ProjectId,
    label: i18n.t('playTurbo.pushDialog.name.code.projectId'),
    name: '',
  },
  {
    code: PushNameCode.ProjectName,
    label: i18n.t('playTurbo.pushDialog.name.code.projectName'),
    name: '',
  },

  {
    code: PushNameCode.Version,
    label: i18n.t('playTurbo.pushDialog.name.code.versionName'),
    name: '',
  },

  {
    code: PushNameCode.OutputName,
    label: i18n.t('playTurbo.pushDialog.name.code.outputName'),
    name: '',
  },

  {
    code: PushNameCode.Platform,
    label: i18n.t('playTurbo.pushDialog.name.code.platform'),
    name: '',
  },

  {
    code: PushNameCode.AutoIncrement,
    label: i18n.t('playTurbo.pushDialog.name.code.autoIncrement'),
    name: '',
  },

  {
    code: PushNameCode.CreativeVersion,
    label: i18n.t('playTurbo.pushDialog.name.code.creative_version'),
    name: '',
  },
]
